import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCsrfToken } from '../utils/auth';
import SearchableSelect from '../components/SearchableSelect';
import OpenFilterLink from '../components/OpenFilterLink';
import TrainingFilterCheck from '../components/TrainingFilterCheck';
import '../styles/TrainingConfig.css';

/**
 * Training Traceability — the config screen.
 *
 * Deliberately flat: no L1..L6 levels, just four filter slots. The two projects are SHOWN,
 * not chosen — the backend resolves "Document Control" and "Training Management" by name and
 * picks up their single version behind the scenes. When a tenant renamed them, resolution
 * fails and we fall back to a project picker rather than dead-ending.
 *
 * Filters use the same graceful degradation as the traceability wizard (v2.25.0): a dropdown
 * when GetFilterList exists, a plain number input when it doesn't.
 *
 * Design: design/DESIGN_TRAINING_MANAGEMENT.md §0.5
 */

const projectLabel = (p) =>
  `${p.project_name}${p.version_label ? ` (${p.version_label})` : ''}`;

const SLOT_HELP = {
  dms: 'Released controlled documents. Their Roles field is the training requirement.',
  users: 'User profiles. Their Job Title / Roles field is the training obligation.',
  tasks: 'Training tasks that exist. The absence of one is the finding.',
  records: 'Collected and saved. Not used by this version — formal training comes later.',
};

/** SearchableSelect takes an array of STRINGS and reports the picked string, so labels are
 *  mapped back to ids here — same shape as ConfigPage's FilterSelect. */
function FilterField({ slot, value, onChange }) {
  const filters = slot.filters || [];
  const labelOf = (f) => `${f.name} (#${f.id})`;
  const byLabel = {};
  const options = [''];
  let current = '';

  filters.forEach((f) => {
    const l = labelOf(f);
    byLabel[l] = f;
    options.push(l);
    if (value && String(f.id) === String(value)) current = l;
  });
  // Keep a saved id that isn't in the returned set (private or deleted filter) selectable,
  // so editing a panel can never silently blank one of its filters.
  if (value && !current) {
    const l = `#${value} (current)`;
    byLabel[l] = { id: value, name: '' };
    options.splice(1, 0, l);
    current = l;
  }

  if (!slot.available || filters.length === 0) {
    // A tenant without GetFilterList, a failed call, and a type Orcanos keeps no saved
    // filters for all render this same box — say which one it was, or a picker that is
    // simply absent reads as a missing feature. Same wording as the wizard (ConfigPage).
    return (
      <>
        <input
          className="tm-num"
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value, '')}
          placeholder="Filter ID, e.g. 865"
        />
        {slot.reason === 'unsupported_type' ? (
          <small className="tm-note">
            Orcanos keeps no saved filters for {slot.type_label || slot.item_type} — enter
            the filter ID.
          </small>
        ) : slot.reason ? (
          <small className="tm-note tm-note-warn">
            Couldn&rsquo;t load the filter list — reload the page to try again, or enter the
            filter ID.
          </small>
        ) : null}
      </>
    );
  }

  return (
    <SearchableSelect
      options={options}
      value={current}
      onChange={(e) => {
        const f = byLabel[e.target.value];
        onChange(f ? String(f.id) : '', f ? f.name : '');
      }}
      placeholder="Search filters..."
    />
  );
}

function TrainingConfig() {
  const navigate = useNavigate();
  const { setupId } = useParams();
  const editing = !!setupId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [setup, setSetup] = useState(null);           // slots + projects from /setup
  const [name, setName] = useState('Training Traceability');
  const [isPublic, setIsPublic] = useState(true);
  const [ownerOnlyEdit, setOwnerOnlyEdit] = useState(true);
  // { dms: {filter_id, filter_name}, users: {...}, tasks: {...}, records: {...} }
  const [picked, setPicked] = useState({});
  // Manual project override, used only when name resolution failed for that project.
  const [projectOverride, setProjectOverride] = useState({});
  // Per-slot column check: kind -> the /check-filter result, and kind -> in-flight flag.
  const [checks, setChecks] = useState({});
  const [checking, setChecking] = useState({});
  // The user's own column mappings: kind -> engine role -> the filter's field Name. Set only
  // when a field could not be found by name OR by label — `Roles`, `Training List` and
  // `Trainees` are per-tenant custom fields, so on some accounts nothing but a human can say
  // which column plays the part. Saved with the panel; the engine prefers it over discovery.
  const [fieldMap, setFieldMap] = useState({});

  // The check is fired from several places (load, a pick, a mapping, Re-check), and each one
  // knows the state it wants checked BETTER than a render-time closure does — so everything it
  // reads is passed in. `pendingRef` keeps a filter changed twice in quick succession from
  // showing the first answer against the second filter.
  const pendingRef = useRef({});

  const runCheck = useCallback(async (kinds, ctx) => {
    const { picked: p, fieldMap: fm, slots: sl, projectOverride: po } = ctx;
    const wanted = [];
    kinds.forEach((kind) => {
      const slot = (sl || []).find((s) => s.key === kind);
      const fid = parseInt(p?.[kind]?.filter_id, 10);
      if (!slot || !fid) return;
      const proj = (po || {})[slot.project_key] || slot;
      wanted.push({
        kind,
        filter_id: fid,
        item_type: slot.item_type,
        version_id: proj.version_id ? String(proj.version_id) : null,
      });
    });
    if (!wanted.length) return;

    const token = {};
    wanted.forEach((w) => { pendingRef.current[w.kind] = token; });
    setChecking((c) => ({ ...c, ...Object.fromEntries(wanted.map((w) => [w.kind, true])) }));

    let results = {};
    try {
      const resp = await fetch('/api/training/check-filter', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({ slots: wanted, fields: fm || {} }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || `Column check failed (${resp.status})`);
      results = data.results || {};
    } catch (err) {
      // A failed check is reported as "couldn't check", never as "nothing missing" — and it
      // never blocks the save, because we have no finding, only an absence of one.
      wanted.forEach((w) => {
        results[w.kind] = { kind: w.kind, checked: false, reason: 'error', message: err.message };
      });
    }

    setChecking((c) => ({ ...c, ...Object.fromEntries(wanted.map((w) => [w.kind, false])) }));
    setChecks((prev) => {
      const next = { ...prev };
      wanted.forEach((w) => {
        if (pendingRef.current[w.kind] === token && results[w.kind]) next[w.kind] = results[w.kind];
      });
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch('/api/training/setup', { credentials: 'include' });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(e.detail || `Failed to read projects (${resp.status})`);
      }
      const data = await resp.json();
      setSetup(data);

      // Pre-select the tool's own filters (exactly one match only — see backend).
      const seed = {};
      (data.slots || []).forEach((s) => {
        if (s.default_filter) {
          seed[s.key] = { filter_id: String(s.default_filter.id), filter_name: s.default_filter.name };
        }
      });

      let final = seed;
      let map = {};

      if (editing) {
        const c = await fetch(`/api/training/${setupId}/config`, { credentials: 'include' });
        if (c.ok) {
          const cfg = await c.json();
          setName(cfg.name || 'Training Traceability');
          setIsPublic(!!cfg.is_public);
          setOwnerOnlyEdit(!!cfg.owner_only_edit);
          const saved = {};
          Object.entries(cfg.sources || {}).forEach(([k, v]) => {
            if (v && v.filter_id) {
              saved[k] = { filter_id: String(v.filter_id), filter_name: v.filter_name || '' };
            }
          });
          final = { ...seed, ...saved };   // the saved panel always wins over the seed
          map = cfg.fields || {};
        }
      }

      setPicked(final);
      setFieldMap(map);
      // Every filter already chosen is checked once the screen is up — an existing panel can
      // have been fine when it was saved and had a column removed from its filter since.
      runCheck(Object.keys(final), {
        picked: final, fieldMap: map, slots: data.slots || [], projectOverride: {},
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [editing, setupId, runCheck]);

  useEffect(() => { load(); }, [load]);

  const slots = setup?.slots || [];
  const projectFor = (slot) => projectOverride[slot.project_key] || {
    project_id: slot.project_id,
    project_name: slot.project_name,
    version_id: slot.version_id,
  };

  const pick = (key, filterId, filterName) => {
    const next = { ...picked, [key]: { filter_id: filterId, filter_name: filterName } };
    setPicked(next);
    // Drop the old answer first — a ✓ left standing under a filter it was never about is
    // exactly the false all-clear this whole check exists to prevent.
    setChecks((c) => { const n = { ...c }; delete n[key]; return n; });
    if (filterId) runCheck([key], { picked: next, fieldMap, slots, projectOverride });
  };

  /** "That column IS the Roles field" — the user answering what discovery could not. */
  const mapField = (kind, role, columnName) => {
    const forKind = { ...(fieldMap[kind] || {}) };
    if (columnName) forKind[role] = columnName;
    else delete forKind[role];
    const next = { ...fieldMap, [kind]: forKind };
    setFieldMap(next);
    runCheck([kind], { picked, fieldMap: next, slots, projectOverride });
  };

  const required = ['dms', 'users', 'tasks'];
  const missing = slots
    .filter((s) => required.includes(s.key) && !picked[s.key]?.filter_id)
    .map((s) => s.label);
  const unresolved = slots.filter((s) => required.includes(s.key) && !projectFor(s).project_id);

  // A filter we CHECKED and found short of a required column builds a report that is wrong
  // rather than empty — someone reads as untrained on a document they signed. That is worth
  // blocking on. A check that could not run blocks nothing: an absence of a finding is not a
  // finding, and a screen that refuses to save because Orcanos was briefly unreachable is
  // worse than the risk it is guarding.
  const shortSlots = slots.filter(
    (s) => picked[s.key]?.filter_id
      && checks[s.key]?.checked
      && (checks[s.key].missing?.required || []).length > 0,
  );
  const canSave = name.trim() && missing.length === 0 && unresolved.length === 0
    && shortSlots.length === 0 && !saving;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const src = (key) => {
      const slot = slots.find((s) => s.key === key) || {};
      const proj = projectFor(slot);
      const p = picked[key] || {};
      return {
        filter_id: p.filter_id ? parseInt(p.filter_id, 10) : null,
        filter_name: p.filter_name || null,
        item_type: slot.item_type,
        project_id: proj.project_id ? String(proj.project_id) : null,
        version_id: proj.version_id ? String(proj.version_id) : null,
      };
    };
    const dmsSlot = slots.find((s) => s.key === 'dms') || {};
    const dmsProj = projectFor(dmsSlot);

    const body = {
      name: name.trim(),
      project_id: String(dmsProj.project_id || ''),
      version_id: String(dmsProj.version_id || ''),
      dms: src('dms'),
      users: src('users'),
      tasks: src('tasks'),
      records: src('records'),
      is_public: isPublic,
      owner_only_edit: ownerOnlyEdit,
      fields: fieldMap,
    };

    try {
      const resp = await fetch(editing ? `/api/training/${setupId}` : '/api/training', {
        method: editing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || `Save failed (${resp.status})`);
      navigate(`/training/${editing ? setupId : data.id}`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="tm-config"><div className="tm-loading">Reading your Orcanos projects…</div></div>;
  }

  return (
    <div className="tm-config">
      <div className="tm-head">
        <button className="tm-back" onClick={() => navigate('/training')}>← Back</button>
        <div>
          <h1>{editing ? 'Edit Training Traceability panel' : 'New Training Traceability panel'}</h1>
          <p className="tm-sub">
            Four filters. The panel computes who holds a role a document names and has
            no training task at all.
          </p>
        </div>
      </div>

      {error && <div className="tm-error">{error}</div>}

      <div className="tm-card">
        <label className="tm-label" htmlFor="tm-name">Panel name</label>
        <input
          id="tm-name"
          className="tm-text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Read &amp; Understand compliance"
        />
      </div>

      {['document_control', 'training_management'].map((groupKey) => {
        const group = slots.filter((s) => s.project_key === groupKey);
        if (!group.length) return null;
        const first = group[0];
        const proj = projectFor(first);
        const resolved = !!proj.project_id;
        const title = groupKey === 'document_control' ? 'Document Control' : 'Training Management';

        // The project is a HEADING over the group, not a card of its own — each work item
        // below it is its own card. Three slots sharing one card read as one setting with
        // three parts; they are three independent filters, each with its own column check,
        // and the check panel is tall enough that the boundaries stopped being legible.
        return (
          <div className="tm-group" key={groupKey}>
            <div className="tm-project">
              <span className="tm-project-eyebrow">Project</span>
              {resolved ? (
                <span className="tm-project-name">
                  {proj.project_name}
                  {proj.version_id && <span className="tm-project-ver">v{proj.version_id}</span>}
                </span>
              ) : (
                <span className="tm-project-name tm-unresolved">{title} — not found</span>
              )}
            </div>

            {!resolved && (
              <div className="tm-fallback">
                <p>
                  No project named “{title}” in this account. Pick the one that holds these items:
                </p>
                <SearchableSelect
                  options={['', ...(setup?.projects || []).map(projectLabel)]}
                  value={projectOverride[groupKey] ? projectLabel(projectOverride[groupKey]) : ''}
                  onChange={(e) => {
                    const p = (setup?.projects || []).find((x) => projectLabel(x) === e.target.value);
                    if (p) setProjectOverride((o) => ({ ...o, [groupKey]: p }));
                  }}
                  placeholder="Pick a project..."
                />
                <p className="tm-hint">
                  Filters can still be entered by ID below.
                </p>
              </div>
            )}

            {group.map((slot) => (
              <div className="tm-card tm-slot" key={slot.key}>
                <div className="tm-slot-head">
                  <span className="tm-slot-label">{slot.label}</span>
                  <span className="tm-slot-type">{slot.type_label || slot.item_type}</span>
                  {slot.key === 'records' && <span className="tm-slot-opt">optional</span>}
                </div>
                <FilterField
                  slot={slot}
                  value={picked[slot.key]?.filter_id || ''}
                  name={picked[slot.key]?.filter_name || ''}
                  onChange={(v, n) => pick(slot.key, v, n)}
                />
                <OpenFilterLink
                  baseUrl={setup?.orcanos_url}
                  versionId={projectFor(slot).version_id}
                  itemTypeCode={slot.item_type}
                  filterId={picked[slot.key]?.filter_id || ''}
                  label={`${slot.label} filter`}
                />
                <p className="tm-hint">{SLOT_HELP[slot.key]}</p>
                <TrainingFilterCheck
                  result={checks[slot.key]}
                  busy={!!checking[slot.key]}
                  hasFilter={!!picked[slot.key]?.filter_id}
                  mapping={fieldMap[slot.key]}
                  onMap={(role, columnName) => mapField(slot.key, role, columnName)}
                  onRecheck={() =>
                    runCheck([slot.key], { picked, fieldMap, slots, projectOverride })}
                />
              </div>
            ))}
          </div>
        );
      })}

      <div className="tm-card">
        <label className="tm-check">
          <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
          <span>Share with everyone in this account</span>
        </label>
        <label className="tm-check">
          <input
            type="checkbox"
            checked={ownerOnlyEdit}
            onChange={(e) => setOwnerOnlyEdit(e.target.checked)}
          />
          <span>Only I can edit this panel</span>
        </label>
      </div>

      <div className="tm-actions">
        {missing.length > 0 && (
          <span className="tm-missing">Still needed: {missing.join(', ')}</span>
        )}
        {missing.length === 0 && shortSlots.length > 0 && (
          <span className="tm-missing">
            Required columns are missing from: {shortSlots.map((s) => s.label).join(', ')} —
            add them to the filter in Orcanos, or map them above.
          </span>
        )}
        <button className="tm-btn-secondary" onClick={() => navigate('/training')}>Cancel</button>
        <button className="tm-btn-primary" disabled={!canSave} onClick={handleSave}>
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Create panel'}
        </button>
      </div>
    </div>
  );
}

export default TrainingConfig;
